import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import * as adminApi from "@/api/admin";
import type { NovaPerk } from "@/api/admin";

function PerkCard({ perk, index }: { perk: NovaPerk; index: number }) {
  const [stat, setStat] = useState(perk.stat);
  const [label, setLabel] = useState(perk.label);
  const [description, setDescription] = useState(perk.description);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  const dirty =
    stat !== perk.stat || label !== perk.label || description !== perk.description;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(false);
    setSaving(true);
    try {
      const updated = await adminApi.updateNovaPerk(perk.id, { stat, label, description });
      // Keep local baseline in sync so the form is no longer "dirty".
      perk.stat = updated.stat;
      perk.label = updated.label;
      perk.description = updated.description;
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div className="flex items-center">
        <h2 className="text-sm font-semibold text-gray-700">Card {index + 1}</h2>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to save this card.</AlertDescription>
        </Alert>
      )}

      <div className="space-y-1">
        <label className="text-xs text-gray-500">Stat</label>
        <Input value={stat} onChange={e => setStat(e.target.value)} placeholder="30.000" />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Label</label>
        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="πόντοι καλωσορίσματος" />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00d9d9]/40 resize-none"
          placeholder="Card description shown under the label."
        />
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving || !dirty} className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white">
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : saved ? (
            <><Check size={14} className="mr-1" /> Saved</>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </form>
  );
}

export function NovaPerksView() {
  const [perks, setPerks] = useState<NovaPerk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    adminApi.getNovaPerks()
      .then(setPerks)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load the perk cards.</AlertDescription>
      </Alert>
    );
  }
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-lg" />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {perks.map((perk, i) => (
        <PerkCard key={perk.id} perk={perk} index={i} />
      ))}
    </div>
  );
}
